import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { Col, Flag, Grid, Icon, Spinner, Typography } from 'gu-ui-kit'
import styles from 'modules/User/Profile/subcomponents/Phone/phone.module.scss'
import { useCountries } from 'modules/Countries/hooks/useCountries'
import { Field, Form, formValueSelector, reduxForm, SubmissionError } from 'redux-form'
import renderMaskedInput from 'components/FormControls/maskedInputField'
import renderSelect from 'components/FormControls/select'
import PropTypes from 'prop-types'
import renderInputField from 'components/FormControls/textInput'
import USER_API_V1 from 'api/user/v1'
//import phoneFieldNormalize from 'utils/normalize/phoneFieldNormalize'
import { notificationShow } from 'modules/Notifications/actions'
import OutsideClickHandler from 'react-outside-click-handler'
import validator from 'utils/asyncValidator'
import * as Yup from 'yup'
import i18n from 'i18next'
import extractErrors from 'utils/extractErrors'
import shouldAsyncValidate from 'utils/shouldAsyncValidate'

const phoneNormalize = (value, prefix = '') => {
  const rules = [
    {
      re: /(\D)/g,
      replacer: '',
    },
    {
      re: /^(.*)$/g,
      replacer: function (digits) {
        return digits ? '+' + prefix + digits : ''
      },
    },
    {
      re: /\s+$/g,
      replacer: '',
    },
  ]

  return rules
    .reduce((value, rule) => value.replace(new RegExp(rule.re), rule.replacer), value)
    .trim()
}

const validationSchema = Yup.object().shape({
  phone_mask: Yup.mixed().nullable(),
  country_code: Yup.mixed().nullable(),
  country_prefix: Yup.mixed().nullable(),
  phone_number: Yup.string()
    .test(
      'phoneValidate',
      'common::validation_messages.incorrect_phone_format',
      function test(value, context) {
        const { path, createError } = context

        const phone_mask = context.parent?.phone_mask || ''
        const country_prefix = context.parent?.country_prefix || ''
        const digitsLength = phone_mask.replace(/\s/g, '').match(/\d/g).length
        const re = new RegExp(`\\+\\d{${digitsLength}}$`, 'g')

        if (!value || value.length === 0) {
          return true
        }

        const correctFormat = re.test(value)

        return (
          correctFormat ||
          createError({
            path,
            message: i18n.t('common::validation_messages.incorrect_phone_mask', {
              phone_mask: phone_mask.replace(/^\+\d+ /, `+${country_prefix} `) || '',
            }),
          })
        )
      },
    )
    .required('common::validation_messages.required'),
})

let PhoneForm = (props) => {
  const { handleSubmit, autofill, form, submitting, touch } = props
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [phonePrefix, setPhonePrefix] = useState()
  const selector = formValueSelector(form)

  const {
    user: { phone = '', phone_country_code = null, loading = false },
    countries: { data: countries = [] } = {},
    formValues: { country_code, phone_mask, phone_number },
  } = useSelector((state) => ({
    user: state['user/profile'],
    countries: state['countries'],
    formValues: selector(state, 'country_code', 'phone_mask', 'phone_number'),
  }))

  const { getData, resetData } = useCountries()

  useEffect(() => {
    getData({ has_phone_codes: true })

    return () => resetData()
  }, [])

  useEffect(() => {
    phone_country_code && autofill('country_code', phone_country_code)
  }, [phone_country_code])

  useEffect(() => {
    autofill('phone_number', phone || '')
    if (phone) {
      touch('phone_number')
    }
  }, [phone])

  const currentCountry = useMemo(
    () => countries.find(({ code }) => code === country_code),
    [countries, country_code],
  )

  useEffect(() => {
    if (currentCountry) {
      const { code, phone: phoneCodeOptions = [] } = currentCountry
      const clearPhone = phone_number.replace('+', '')
      const phoneOption = phoneCodeOptions.find(({ code }) => clearPhone.indexOf(code) === 0)
      const { mask, code: prefix } = phoneOption ? phoneOption : phoneCodeOptions[0]

      !phoneOption && autofill('phone_number', '')

      setPhonePrefix(prefix)

      if (code) {
        autofill('country_code', code)
        autofill('country_prefix', prefix)
      }

      autofill('phone_mask', mask ? mask.replace(/[-]/g, ' ') : [])
    }
  }, [currentCountry, phone_number])

  const countriesOptions = useMemo(
    () =>
      countries.reduce(
        (acc, { code, phone = [] }) => [
          ...acc,
          ...phone.map(({ code: phoneCode, mask: phoneMask = null }) => ({
            code,
            phoneCode,
            phoneMask,
            value: code,
            label: t(`countries::${code}`),
          })),
        ],
        [],
      ),
    [countries],
  )

  const [editMode, setEditMode] = useState(false)

  const submit = useCallback(async () => {
    const { error } = await USER_API_V1.updatePhoneNumber({
      data: {
        phone_country_code: country_code,
        phone_number,
      },
    })

    if (error) {
      const { fields } = extractErrors(error.errors)

      dispatch(
        notificationShow({
          type: 'error',
          message: t('user::settings.profile.phone.notification.error'),
        }),
      )

      throw new SubmissionError(fields)
    } else {
      setEditMode(false)
      dispatch(
        notificationShow({
          type: 'success',
          message: t('user::settings.profile.phone.notification.success'),
        }),
      )
    }
  }, [phone_number, country_code])

  const re = useMemo(() => new RegExp(`^(([+| ]?)(${phonePrefix})?)`, 'g'), [phonePrefix])

  return (
    <Form onSubmit={handleSubmit(submit)}>
      <Grid gap={16} wrap={false}>
        <Col flexGrow={0}>
          <Field
            name="country_code"
            mode={'filter'}
            component={renderSelect}
            controlClassname={styles['registration__control-component']}
            dropdownClassName={styles['registration__select-dropdown']}
            showEmptyOption={false}
            noResultsLabel={t('auth::forms.registration.fields.country_code.no_result')}
            filterPlaceholder={t('auth::forms.registration.fields.country_code.filter_placeholder')}
            fullWidth
            t={t}
            onFocus={() => setEditMode(true)}
            options={countriesOptions}
            ControlProps={{
              startAdornment: country_code ? (
                <Flag name={country_code} />
              ) : (
                <Icon size={'m'} name={'globe'} color={'Label-Fifth'} />
              ),
            }}
            activeOptionLabelTemplate={({ phoneCode }) => `+${phoneCode}`}
            optionLabelTemplate={({ phoneCode, code }) => (
              <Grid justifyContent={'between'} alignItems={'center'} wrap={false} gap={16}>
                <Grid wrap={false} gap={8} justifyContent={'start'} alignItems={'center'}>
                  <Flag name={code} width={24} />

                  <Typography
                    variant={'Body-15'}
                    color={'Label-Fifth'}
                    noWrap
                    className={styles['country-name']}
                  >
                    {t(`countries::${code}`)}
                  </Typography>
                </Grid>

                <Typography noWrap variant={'Body-15'} color={'Label-Secondary'}>
                  +{phoneCode}
                </Typography>
              </Grid>
            )}
            noLabel
          />
        </Col>
        <Col flexGrow={1}>
          <OutsideClickHandler onOutsideClick={() => setEditMode(false)}>
            <Field
              prefix={phonePrefix ? `+${phonePrefix}` : ''}
              fullWidth
              parse={(value) => value.replace(re, '').replace(/\D/g, '').trim()}
              normalize={(value) => phoneNormalize(value, phonePrefix)}
              format={(value) => value?.replace(re, ' ').replace(/\D/g, '').trim() || ''}
              name="phone_number"
              component={!phone_country_code ? renderInputField : renderMaskedInput}
              mask={phone_mask
                .replace(/^([+]| )\d+ /g, ' ')
                .split('')
                .map((d) => (d === '9' ? /[0-9]/ : d))}
              inputMode={'tel'}
              label={t('auth::forms.registration.fields.phone.label')}
              t={t}
              onFocus={() => setEditMode(true)}
              onKeyDown={(e) => e.keyCode === 13 && props.submit()}
              InputProps={{
                startAdornment:
                  loading || submitting ? <Spinner width={16} color={'Label-GU-Primary'} /> : null,
                endAdornment: submitting ? (
                  <div className={styles['edit-button']}>
                    <Spinner width={16} />
                  </div>
                ) : (
                  editMode && (
                    <div className={styles['edit-button']} onClick={() => props.submit()}>
                      <Icon name={'check'} size={'m'} color={'Interface-Seventh'} />
                    </div>
                  )
                ),
              }}
            />
          </OutsideClickHandler>
        </Col>
      </Grid>
    </Form>
  )
}

PhoneForm = reduxForm({
  form: 'PhoneForm',
  initialValues: {
    phone_mask: '',
    phone_number: '',
    country_code: '',
    country_prefix: undefined,
  },
  shouldAsyncValidate: shouldAsyncValidate,
  asyncValidate: validator(validationSchema),
})(PhoneForm)

PhoneForm.propTypes = {
  autofill: PropTypes.func,
  touch: PropTypes.func,
  form: PropTypes.string,
  handleSubmit: PropTypes.func,
  submit: PropTypes.func,
  submitting: PropTypes.bool,
}

export default PhoneForm
